/**
 * What may be dropped where — and, when it may not, the REASON, worded by whoever shows it.
 *
 * View ADR §11's shared rule is the whole of this module's purpose:
 *
 * > A refused target renders as refused *during* the drag — no indicator, no cursor change — rather
 * > than accepting the drop and erroring afterwards.
 *
 * That is decision 8 one layer down ("no control is enabled whose action the server will refuse"),
 * and it is why the gate is a pure predicate over the draft rather than a check inside the mutator.
 * The mutator returns quietly on an illegal move because it must; this is the same question asked
 * where an answer can be *shown*, in the same place for a pointer, a keyboard and a menu.
 *
 * ── The reasons, and what each of them is protecting ─────────────────────────────────────────
 *
 * | Reason | The rule behind it |
 * |---|---|
 * | `notWeekPattern` | A day on the rhythm TAPE carries rest-day spacing and no weekday, so "move to Friday" names nothing |
 * | `notTapePattern` | The inverse: a day on the week GRID is placed by its weekday, so "move to position 3" names nothing. Both exist because the two moves are separate commands and each has to be able to refuse the other's canvas |
 * | `crossPhase` | A progression chain does not cross a phase boundary, and an override's `cycleIndex` is absolute |
 * | `targetDayFull` | `PostSessionTemplateInput.Exercises` caps at `exercisesPerSession`; the save is refused over rows nothing on screen showed |
 * | `unknownTarget` | The token names nothing — a stale drag over a day another tab deleted, or an undo that landed mid-gesture |
 *
 * ── What is deliberately NOT a refusal ───────────────────────────────────────────────────────
 *
 * · **A drop that changes nothing.** Dropping a block on its own position, or in the gap directly
 *   below it, is a legal drop with no effect — not a target to grey out. The operations rewrite the
 *   array to exactly what it was, Immer records no patch, and the plan does not read as unsaved.
 *   Marking it refused would put a dead zone around every item a trainer picked up.
 * · **A ROLE change, except on the warm-up zone.** A main-work block dropped elsewhere in the main
 *   work is a position change and nothing else — the exercise keeps its role, its counting and its
 *   muscle attribution. Until 29 October 2026 the day's warm-up block took no block drop at all, so
 *   the ⋯ role toggle was the only way across. The owner reversed that on 29 October 2026: a block
 *   dropped on the warm-up zone of its own day becomes warm-up (`moveExerciseToWarmup`), and stops
 *   counting exactly as the ⋯ toggle would make it. That is the one drop that changes a role, and it
 *   is the zone's whole meaning rather than a side effect of a reorder, so it refuses nothing beyond
 *   a stale token. The warm-up rows themselves still carry no grip — the canon draws them as a name
 *   list with no position numbers — so leaving the warm-up stays the ⋯'s „Do pracy głównej".
 * · **A running client's history.** Moving a day, moving an exercise into or out of a day the client
 *   has trained, and inserting a library pick there are all ordinary forward-only edits (R3): the
 *   backend regenerates only upcoming, not-started workouts and every finished one is a frozen
 *   snapshot. Owner decision, 15 September 2026, matching mobile's `livePlan.ts` ("the backend is
 *   the sole authority"). What the backend does refuse on a live fork is the PHASE structure over the
 *   consumed prefix, which is `phaseEdits.ts`'s question, not a drop's.
 * · **An occupied weekday.** Two days may not share one, so an occupied target SWAPS — see
 *   `moveDayToWeekday`. Refusing would make a fully-programmed week the one week nothing can be
 *   rearranged in.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. The words for a refusal belong to
 * whichever surface shows it.
 */

import { COLLECTION_CAPS } from './planCaps';
import type { DayDraft, PhaseDraft, PlanDraft } from './planDraft.types';

export type DropRefusal =
  | 'notWeekPattern'
  | 'notTapePattern'
  | 'crossPhase'
  | 'targetDayFull'
  | 'unknownTarget';

/** The phase a day sits in, with its index — every gate needs the phase, not the day. */
interface DaySite {
  readonly phase: PhaseDraft;
  readonly day: DayDraft;
}

export function locateDaySite(draft: PlanDraft, daySlotToken: string): DaySite | null {
  for (const phase of draft.phases) {
    for (const day of phase.days) {
      if (day.slotToken === daySlotToken) return { phase, day };
    }
  }
  return null;
}

export function locateExerciseDay(draft: PlanDraft, exerciseSlotToken: string): DaySite | null {
  for (const phase of draft.phases) {
    for (const day of phase.days) {
      if (day.exercises.some((exercise) => exercise.slotToken === exerciseSlotToken)) {
        return { phase, day };
      }
    }
  }
  return null;
}

/**
 * May a day be lifted at all? The answer the grip reads before it renders.
 *
 * Mode-blind: WHICH move the lift becomes is the surface's question, and each command's own gate
 * answers it (`weekdayDropRefusal`, `positionDropRefusal`). A running fork no longer locks it — see
 * the header.
 */
export function canLiftDay(draft: PlanDraft, daySlotToken: string): boolean {
  return locateDaySite(draft, daySlotToken) !== null;
}

/** May this exercise be lifted? False for a standalone day warm-up, which occupies no position. */
export function canLiftExercise(draft: PlanDraft, exerciseSlotToken: string): boolean {
  const site = locateExerciseDay(draft, exerciseSlotToken);
  if (site === null) return false;
  const exercise = site.day.exercises.find((node) => node.slotToken === exerciseSlotToken);
  // A grouped warm-up IS liftable: it is a mini-group member and the frame carries the grip.
  return exercise !== undefined && !(exercise.role === 'Warmup' && !exercise.group);
}

/** Whether a day may land on this weekday. `null` when it may. */
export function weekdayDropRefusal(
  draft: PlanDraft,
  daySlotToken: string,
  targetWeekday: number,
): DropRefusal | null {
  if (draft.mode !== 'date') return 'notWeekPattern';
  const site = locateDaySite(draft, daySlotToken);
  if (site === null) return 'unknownTarget';
  if (!Number.isInteger(targetWeekday) || targetWeekday < 1 || targetWeekday > 7) {
    return 'unknownTarget';
  }
  return null;
}

/**
 * Whether a day may land at this POSITION in its phase — the tape's reorder. `null` when it may.
 *
 * The sequence counterpart of `weekdayDropRefusal`. The target is an index among the phase's own
 * days, so a day can only ever move WITHIN its phase: there is no cross-phase position, and a
 * sequence progression chain does not cross a phase boundary anyway.
 */
export function positionDropRefusal(
  draft: PlanDraft,
  daySlotToken: string,
  targetIndex: number,
): DropRefusal | null {
  if (draft.mode !== 'selfpaced') return 'notTapePattern';
  const site = locateDaySite(draft, daySlotToken);
  if (site === null) return 'unknownTarget';
  if (!Number.isInteger(targetIndex) || targetIndex < 0 || targetIndex >= site.phase.days.length) {
    return 'unknownTarget';
  }
  return null;
}

/**
 * Whether an exercise block may land in this day. `null` when it may.
 *
 * The cap is measured against the whole block, because a superset lands as two rows. `movingCount`
 * is `0` for a same-day move — nothing arrives that was not already there — which is what stops a
 * reorder inside a day that is exactly at the cap from being refused.
 *
 * A day the client has already trained is NOT a refusal (owner, 15 September 2026): the move only
 * reaches the upcoming workouts the backend regenerates, and the finished ones keep their snapshot.
 */
export function exerciseDropRefusal(
  draft: PlanDraft,
  exerciseSlotToken: string,
  targetDaySlotToken: string,
  movingCount: number,
): DropRefusal | null {
  const source = locateExerciseDay(draft, exerciseSlotToken);
  const target = locateDaySite(draft, targetDaySlotToken);
  if (source === null || target === null) return 'unknownTarget';
  if (source.phase !== target.phase) return 'crossPhase';
  if (source.day.slotToken === target.day.slotToken) return null;
  if (target.day.exercises.length + movingCount > COLLECTION_CAPS.exercisesPerSession.max) {
    return 'targetDayFull';
  }
  return null;
}

/**
 * Whether a library pick may be inserted into this day. `null` when it may.
 *
 * The day's exercise cap is the one refusal. It used to also ask whether the day had been trained,
 * and before that whether its phase had been consumed; both were withdrawn on 15 September 2026,
 * because the backend accepts the insert on a live fork and regenerates only upcoming workouts.
 *
 * `arrivingCount` is how many movements this act brings — one for a press, a drop or the `E`
 * shortcut, and the size of the selection for the library's batch add (16 September 2026). It is
 * asked ONCE for the whole batch rather than once per pick, because a batch that would overrun the
 * cap is refused whole: inserting the first three of five and then stopping would leave the trainer
 * with a partial act nothing on screen described, and an undo that only takes back the part that
 * landed.
 */
export function libraryDropRefusal(
  draft: PlanDraft,
  targetDaySlotToken: string,
  arrivingCount = 1,
): DropRefusal | null {
  const target = locateDaySite(draft, targetDaySlotToken);
  if (target === null) return 'unknownTarget';
  if (target.day.exercises.length + arrivingCount > COLLECTION_CAPS.exercisesPerSession.max) {
    return 'targetDayFull';
  }
  return null;
}
